import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { UserPlus, CheckCircle, ArrowRight, MapPin, Calendar as CalendarIcon, AlertTriangle, AlertCircle, Mail, Clock, Hourglass } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import LiveClock from '../components/common/LiveClock';
import Footer from '../components/common/Footer';
import { eventAPI, attendeeAPI, registrationLinkAPI } from '../api/endpoints';
import { useToastContext } from '../context/ToastContext';
import { APP_NAME } from '../utils/constants';
import { getActiveUniqueFields, findFieldByName } from '../utils/fieldHelpers';
import { validateAttendeeDataFormat } from '../utils/validateDataFormat';
import CountrySelect from '../components/common/CountrySelect';
import defaultLogo from '../assets/logo_white_bg.png';
import { ticketAPI } from '../api/endpoints';

const SelfRegistration = () => {
  const toast = useToastContext();
  const [searchParams] = useSearchParams();
  const [selectedEvent, setSelectedEvent] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [registeredName, setRegisteredName] = useState('');
  const [emailNotificationScheduled, setEmailNotificationScheduled] = useState(false);
  const [emailConsent, setEmailConsent] = useState(true); // Default to checked
  const [selectedTicketId, setSelectedTicketId] = useState('');
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Load Paystack script dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    document.body.appendChild(script);
    return () => { document.body.removeChild(script); };
  }, []);

  // Fetch registration configuration for selected event (public endpoint)
  const { data: registrationData, isLoading: fieldsLoading, error: fieldsError } = useQuery({
    queryKey: ['registration-config', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { data: {} };

      // Check if we have a token (secure registration link)
      const tokenFromUrl = searchParams.get('token');
      if (tokenFromUrl) {
        // Use token-based API endpoint
        const response = await registrationLinkAPI.getConfigByToken(tokenFromUrl);
        return response;
      } else {
        // Use event ID based API endpoint (backward compatibility)
        const response = await eventAPI.getRegistrationConfig(selectedEvent);
        return response.data;
      }
    },
    enabled: !!selectedEvent,
  });

  // Fetch public tickets
  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['public-tickets', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { data: [] };
      const response = await ticketAPI.getPublicTickets(selectedEvent);
      return response.data || [];
    },
    enabled: !!selectedEvent,
  });
  const publicTickets = ticketsData || [];

  // Auto-select event from URL parameter - REQUIRED for security
  useEffect(() => {
    const eventFromUrl = searchParams.get('event');
    const tokenFromUrl = searchParams.get('token');

    if (eventFromUrl && !selectedEvent) {
      setSelectedEvent(eventFromUrl);
    } else if (tokenFromUrl && !selectedEvent) {
      // For token-based registration, use the token as the identifier
      setSelectedEvent(tokenFromUrl);
    }
  }, [searchParams, selectedEvent]);

  // Reset form when event changes
  useEffect(() => {
    setFormData({});
    setErrors({});
  }, [selectedEvent]);


  // Register mutation - use public self-registration endpoint
  const registerMutation = useMutation({
    mutationFn: (data: any) => attendeeAPI.selfRegister(selectedEvent, data),
    onSuccess: async (response) => {
      // Check if payment is required (new flow)
      if (response.data?.requires_payment) {
        setIsProcessingPayment(true);
        const paymentData = response.data.payment_data;
        
        // Launch Paystack Pop
        const handler = (window as any).PaystackPop.setup({
          key: paymentData.paystack_public_key,
          email: paymentData.email,
          amount: Math.round(paymentData.amount * 100), // convert to pesewas
          currency: 'GHS',
          ref: paymentData.reference,
          callback: async function(verifyResponse: any) {
            try {
              const verifyRes = await ticketAPI.verifyPayment(selectedEvent, paymentData.reference);
              if (verifyRes.data?.status === 'success' || verifyRes.status === 'success' || (verifyRes as any).success) {
                toast.success('Payment verified successfully! You are now registered.');
                
                // Get the name for the success screen
                const uniqueFields = getActiveUniqueFields(registrationData);
                const nameField = findFieldByName(uniqueFields, 'full_name');
                const name = formData[nameField?.id] || 'Guest';
                setRegisteredName(name);
                
                setShowSuccess(true);
                setFormData({});
                setErrors({});
                setSelectedTicketId('');
                setTicketQuantity(1);
                setIsProcessingPayment(false);
              } else {
                toast.error('Payment verification failed.');
                setIsProcessingPayment(false);
              }
            } catch (e) {
              toast.error('Error verifying payment.');
              setIsProcessingPayment(false);
            }
          },
          onClose: function() {
            toast.info('Payment cancelled. Your registration was not saved.');
            setIsProcessingPayment(false);
          }
        });
        handler.openIframe();
        return; // Stop further execution, wait for payment
      }

      // Standard free/no-ticket registration flow
      const attendee = response.data?.attendee;
      const attendeeData = response.data;
      
      const uniqueFields = getActiveUniqueFields(registrationData);
      const nameField = findFieldByName(uniqueFields, 'full_name');
      const name = formData[nameField?.id] || 'Guest';
      setRegisteredName(name);

      // Check debug info for email sending status
      if (attendeeData?.debug_info?.email_sent) {
        setEmailNotificationScheduled(true);
      } else {
        setEmailNotificationScheduled(false);
      }

      toast.success('Registration successful!');
      setShowSuccess(true);
      setFormData({});
      setErrors({});
      setSelectedTicketId('');
      setTicketQuantity(1);

      // Auto-hide success message after 8 seconds (longer for email notification message)
      setTimeout(() => {
        setShowSuccess(false);
        setEmailNotificationScheduled(false);
      }, 8000);
    },
    onError: (error) => {
      console.error('Registration error:', error);

      // Enhanced error handling for 422 validation errors
      if ((error as any).response?.status === 422) {
        const validationErrors = (error as any).response?.data?.errors || {};
        console.error('Validation errors:', validationErrors);

        // Show specific validation errors
        const errorMessages = Object.values(validationErrors).flat() as string[];
        if (errorMessages.length > 0) {
          const hasDuplicateError = errorMessages.some(msg => 
            msg.toLowerCase().includes('already been taken') || 
            msg.toLowerCase().includes('unique')
          );
          
          if (hasDuplicateError) {
            toast.error('Someone with this email or phone or both is already registered or has used this email or phone to register for this event. Please check and try again.');
          } else {
            toast.error(`Validation Error: ${errorMessages.join(', ')}`);
          }
        } else {
          toast.error('Please check your form data and try again.');
        }
      } else {
        toast.error(error.message || 'Registration failed');
      }
    },
  });

  const handleChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const fields = getActiveUniqueFields(registrationData);

    // Debug logging to see what fields we're validating
    console.log('🔍 SelfRegistration validation fields:', fields.map(f => ({
      name: f.field_name,
      label: f.field_label,
      active: f.is_active,
      required: f.is_required,
      type: f.field_type,
      value: formData[f.id]
    })));

    fields.forEach(field => {
      // Only validate if the field is active
      if (!field.is_active) return;

      // Check if the field is required - respect the backend's is_required flag
      const isRequired = field.is_required || field.is_mandatory_by_event;
      const hasValue = formData[field.id] !== undefined && formData[field.id] !== '';

      // Validate required fields that don't have a value
      if (isRequired && !hasValue) {
        console.log(`❌ Field required but empty: ${field.field_name} (${field.field_label}), required: ${isRequired}`);
        newErrors[field.id] = `${field.field_label} is required`;
        return;
      }

      // Skip further validation if field is empty and not required
      if (!hasValue) return;

      // Email validation (only if field has a value)
      if (field.field_type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData[field.id])) {
          newErrors[field.id] = 'Invalid email address';
        }
      }

      // Phone validation (only if field has a value)
      if (field.field_type === 'phone') {
        const phoneRegex = /^[\d\s-+()]+$/;
        if (!phoneRegex.test(formData[field.id])) {
          newErrors[field.id] = 'Invalid phone number';
        }
      }
    });

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedEvent) {
      setErrors({ event: 'Please select an event' });
      return;
    }

    if (currentEvent?.is_ticketing_enabled && publicTickets.length > 0 && !selectedTicketId) {
      setErrors({ ticket: 'Please select a ticket type' });
      toast.error('Please select a ticket type');
      return;
    }

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Format data for submission using active fields only - SAME AS AddAttendeeModal
    const uniqueFields = getActiveUniqueFields(registrationData);

    // Validate and format data for Laravel backend (same as AddAttendeeModal)
    const validation = validateAttendeeDataFormat(formData, uniqueFields);

    if (!validation.isValid) {
      console.error('❌ Data validation failed:', validation.errors);
      setErrors({ general: validation.errors.join(', ') });
      return;
    }

    // Add any additional data that might be expected by the backend - SAME AS AddAttendeeModal
    const attendeeData = {
      ...validation.data,
      email_consent: uniqueFields.some(field => field.field_type === 'email') 
        ? (currentEvent?.email_notification_setting === 'opt_in' ? emailConsent : currentEvent?.email_notification_setting === 'always') 
        : false,
      ticket_type_id: currentEvent?.is_ticketing_enabled ? (selectedTicketId || null) : null,
    };

    console.log('🚀 SelfRegistration - Sending validated attendee data:', attendeeData);
    console.log('🚀 SelfRegistration - Original form data:', formData);
    console.log('🚀 SelfRegistration - Validation result:', validation);
    console.log('🚀 SelfRegistration - Selected event:', selectedEvent);
    console.log('🚀 SelfRegistration - Unique fields:', uniqueFields.map(f => ({ id: f.id, name: f.field_name, active: f.is_active })));

    registerMutation.mutate(attendeeData);
  };

  const renderField = (field) => {
    // SAME LOGIC AS AttendeeModal
    const commonProps = {
      label: field.field_label,
      value: formData[field.id] || '',
      onChange: (e) => handleChange(field.id, e.target.value),
      error: errors[field.id],
      required: field.is_required, // Use field.is_required directly like AttendeeModal
      placeholder: field.placeholder || `Enter ${field.field_label.toLowerCase()}`,
    };

    switch (field.field_type) {
      case 'text':
        return <Input key={field.id} {...commonProps} type="text" />;
      case 'email':
        return <Input key={field.id} {...commonProps} type="email" />;
      case 'phone':
        return <Input key={field.id} {...commonProps} type="tel" />;
      case 'number':
        return <Input key={field.id} {...commonProps} type="number" />;
      case 'textarea':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.field_label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              placeholder={commonProps.placeholder}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors[field.id] && (
              <p className="text-red-500 text-sm mt-1">{errors[field.id]}</p>
            )}
          </div>
        );
      case 'dropdown':
      case 'select':
        // Parse options safely - handle both field.options and field.field_options
        let options = [];
        const optionsData = field.field_options || field.options;
        if (optionsData) {
          if (typeof optionsData === 'string') {
            try {
              options = JSON.parse(optionsData);
            } catch (e) {
              options = [];
            }
          } else if (Array.isArray(optionsData)) {
            options = optionsData;
          }
        }
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {field.field_label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={formData[field.id] || ''}
              onChange={(e) => handleChange(field.id, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Select {field.field_label}</option>
              {options.map((option, idx) => (
                <option key={idx} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors[field.id] && (
              <p className="text-red-500 text-sm mt-1">{errors[field.id]}</p>
            )}
          </div>
        );
      case 'checkbox':
        return (
          <div key={field.id} className="flex items-center">
            <input
              type="checkbox"
              id={field.id}
              checked={formData[field.id] === 'true' || formData[field.id] === true}
              onChange={(e) => handleChange(field.id, e.target.checked.toString())}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor={field.id} className="ml-2 block text-sm text-gray-700">
              {field.field_label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
          </div>
        );
      case 'date':
        return <Input key={field.id} {...commonProps} type="date" />;
      case 'country':
        return (
          <CountrySelect
            key={field.id}
            {...commonProps}
            onChange={(val) => handleChange(field.id, val)}
          />
        );
      default:
        return <Input key={field.id} {...commonProps} type="text" />;
    }
  };

  const currentEvent = registrationData?.event || null;
  const fieldsData = registrationData?.custom_fields || [];
  // Fix: getActiveUniqueFields expects { custom_fields: [...] } structure, so pass registrationData directly
  const activeFields = getActiveUniqueFields(registrationData);

  // Check if event ID or token is required but not provided
  const eventIdFromUrl = searchParams.get('event');
  const tokenFromUrl = searchParams.get('token');
  // Only show "not found" if we have a response but no event, or no event ID/token provided
  const showEventNotFound = (!eventIdFromUrl && !tokenFromUrl) || (registrationData && !fieldsLoading && !currentEvent);

  // Debug logging in development
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 SelfRegistration Debug:', {
      selectedEvent,
      registrationData,
      fieldsData,
      activeFields: activeFields.length,
      fieldsLoading,
      fieldsError,
      showEventNotFound,
      eventFromUrl: searchParams.get('event')
    });

    // Additional debug for fields
    if (fieldsData && fieldsData.length > 0) {
      console.log('🔍 SelfRegistration Fields Detail:', fieldsData.map(f => ({
        id: f.id,
        name: f.field_name,
        label: f.field_label,
        active: f.is_active,
        required: f.is_required,
        type: f.field_type
      })));
    }
  }

  // Loading Screen with Skeleton
  if (fieldsLoading && eventIdFromUrl) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 py-8 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Clock and Event Info Skeleton */}
              <div className="lg:col-span-1 space-y-6">
                {/* Live Clock Skeleton */}
                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-16 mb-4"></div>
                    <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                </div>

                {/* Event Info Skeleton */}
                <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                  <div className="animate-pulse">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                      <div>
                        <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
                        <div className="h-3 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                    <div className="pt-4 border-t border-gray-100">
                      <div className="h-6 bg-gray-200 rounded w-48 mb-3"></div>
                      <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-32"></div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-24"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Registration Form Skeleton */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
                  {/* Form Header */}
                  <div className="bg-primary-600 text-white p-6">
                    <div className="animate-pulse">
                      <div className="h-8 bg-primary-500 rounded w-32 mb-2"></div>
                      <div className="h-4 bg-primary-500 rounded w-48"></div>
                    </div>
                  </div>

                  {/* Form Content Skeleton */}
                  <div className="p-8">
                    <div className="animate-pulse space-y-6">
                      {/* Form Fields Skeleton */}
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i}>
                          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                          <div className="h-10 bg-gray-200 rounded w-full"></div>
                        </div>
                      ))}

                      {/* Email Consent Skeleton */}
                      <div className="border-t pt-6">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="w-4 h-4 bg-gray-200 rounded mt-1"></div>
                            <div className="flex-1">
                              <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                              <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
                              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Submit Button Skeleton */}
                      <div className="pt-6">
                        <div className="h-12 bg-gray-200 rounded w-full"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <Footer variant="registration" />
      </div>
    );
  }

  // Success Screen
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-12 w-12" style={{ color: currentEvent?.primary_color || '#16a34a' }} />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Welcome, {registeredName}!
              </h2>
              <p className="text-lg text-gray-600">
                You've been successfully registered for
              </p>
              <p className="text-xl font-semibold mt-2" style={{ color: currentEvent?.primary_color || '#0d9488' }}>
                {currentEvent?.name}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                Please proceed to the check-in desk to collect your badge.
              </p>
            </div>

            {emailNotificationScheduled && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center mb-2">
                  <Mail className="h-5 w-5 text-green-600 mr-2" />
                  <p className="text-sm font-medium text-green-800">
                    Confirmation Email Scheduled
                  </p>
                </div>
                <p className="text-xs text-green-700">
                  A confirmation email with your registration details will be sent to you shortly.
                </p>
              </div>
            )}

            <Button
              onClick={() => setShowSuccess(false)}
              className="w-full gap-2"
            >
              Register Another Attendee
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
        </div>
        <Footer variant="registration" />
      </div>
    );
  }

  // Event Not Found / 404 Screen
  if (showEventNotFound) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
            <div className="mb-6">
              <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="h-12 w-12 text-red-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                Event Not Found
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                {(!eventIdFromUrl && !tokenFromUrl)
                  ? "No event ID or registration token provided in the URL. Registration requires a specific event ID or valid token."
                  : "The requested event was not found or is not available for registration."
                }
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700">
                Please contact the event organizer for the correct registration link.
              </p>
            </div>


          </div>
        </div>
        <Footer variant="registration" />
      </div>
    );
  }
  // Rich Registration Status Screens
  const registrationStatus = registrationData?.registration_status;

  if (registrationStatus && registrationStatus !== 'open' && !fieldsLoading) {
    const bgColors: Record<string, string> = {
      deactivated: 'bg-orange-50',
      not_yet_open: 'bg-blue-50',
      closed: 'bg-gray-50',
    };
    const borderColors: Record<string, string> = {
      deactivated: 'border-orange-200',
      not_yet_open: 'border-blue-200',
      closed: 'border-gray-200',
    };
    const icons: Record<string, React.ReactNode> = {
      deactivated: <AlertTriangle className="h-16 w-16 text-orange-400" />,
      not_yet_open: <CalendarIcon className="h-16 w-16 text-blue-400" />,
      closed: <AlertCircle className="h-16 w-16 text-gray-400" />,
    };
    const titles: Record<string, string> = {
      deactivated: 'Registration Closed',
      not_yet_open: 'Registration Not Yet Open',
      closed: 'Registration Has Ended',
    };
    const subtitles: Record<string, string> = {
      deactivated: 'Registration for this event has been deactivated by the organizers.',
      not_yet_open: `Registration opens on ${registrationData?.registration_opens_at
        ? new Date(registrationData.registration_opens_at).toLocaleString(undefined, {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })
        : 'a future date'}.`,
      closed: `Registration closed on ${registrationData?.registration_closes_at
        ? new Date(registrationData.registration_closes_at).toLocaleString(undefined, {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })
        : 'a previous date'}.`,
    };

    const ev = registrationData?.event;

    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden w-full max-w-lg">
            {/* Event branding header */}
            <div
              className="p-8 text-white text-center"
              style={{ backgroundColor: ev?.primary_color || '#0d9488' }}
            >
              {ev?.logo_url && (
                <img src={ev.logo_url} alt={ev.name} className="h-16 w-auto object-contain mx-auto mb-4" />
              )}
              <h1 className="text-2xl font-bold">{ev?.name || 'Event'}</h1>
              {ev?.location && (
                <p className="text-sm mt-1 opacity-80 flex items-center justify-center gap-1">
                  <MapPin className="h-3 w-3" /> {ev.location}
                </p>
              )}
            </div>

            {/* Status body */}
            <div className={`${bgColors[registrationStatus]} ${borderColors[registrationStatus]} border-t-4 p-8 text-center`}>
              <div className="flex justify-center mb-4">
                {icons[registrationStatus]}
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                {titles[registrationStatus]}
              </h2>
              <p className="text-gray-600 text-sm mb-6">
                {subtitles[registrationStatus]}
              </p>
              <p className="text-xs text-gray-400">
                Please contact the event organizers for more information.
              </p>
            </div>
          </div>
        </div>
        <Footer variant="registration" />
      </div>
    );
  }

  // Registration Form
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Clock and Event Info */}
            <aside className="lg:col-span-1 space-y-6" aria-label="Event Information">
              {/* App Logo */}
              <div className="flex justify-center lg:justify-start">
                <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-center w-full">
                  <img
                    src={currentEvent?.logo_url || currentEvent?.organization_logo || defaultLogo}
                    alt={`${currentEvent?.name || APP_NAME} logo`}
                    className="max-h-24 w-auto object-contain"
                  />
                </div>
              </div>

              {/* Live Clock */}
              <LiveClock customColor={currentEvent?.primary_color} event={currentEvent} />

              {/* Event Info Card */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-gray-100 rounded-lg">
                    <UserPlus className="h-6 w-6" style={{ color: currentEvent?.primary_color || '#0d9488' }} />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Event Registration</h2>
                    <p className="text-sm text-gray-500">Quick & Easy</p>
                  </div>
                </div>
                <div className="pt-4 border-t border-gray-100 space-y-3">
                  <h3 className="font-semibold text-gray-900 text-base">
                    {currentEvent?.name || 'Loading event...'}
                  </h3>
                  {currentEvent?.description && (
                    <p className="text-sm text-gray-600 mb-3 text-justify whitespace-pre-line leading-relaxed">
                      {currentEvent.description}
                    </p>
                  )}
                  {currentEvent?.location && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-4 w-4 text-teal-600 flex-shrink-0" />
                      <span>{currentEvent.location}</span>
                    </div>
                  )}
                  {(currentEvent?.start_date || currentEvent?.end_date) && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 text-teal-600 flex-shrink-0" />
                        <span>
                          {currentEvent?.start_date && new Date(currentEvent.start_date).toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                          {currentEvent?.end_date &&
                            new Date(currentEvent.end_date).toLocaleDateString() !== new Date(currentEvent.start_date).toLocaleDateString() && (
                              <>
                                {' - '}
                                {new Date(currentEvent.end_date).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric'
                                })}
                              </>
                            )}
                        </span>
                      </div>

                    </div>
                  )}
                </div>
              </div>
            </aside>

            {/* Right Column - Registration Form */}
            <main className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200" role="main">
                {/* Form Header */}
                <div className="text-white p-6" style={{ backgroundColor: currentEvent?.primary_color || '#0d9488' }}>
                  <h1 className="text-2xl font-bold mb-1">Register Now</h1>
                  <p className="text-white text-opacity-90 text-sm">
                    Fill in your details to complete registration
                  </p>
                </div>

                {/* Form Content */}
                <form onSubmit={handleSubmit} className="p-8">
                  {fieldsLoading ? (
                    <div className="text-center py-12 text-gray-500">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-300 mx-auto mb-4" style={{ borderTopColor: currentEvent?.primary_color || '#0d9488' }}></div>
                      <p>Loading registration form...</p>
                    </div>
                  ) : fieldsError ? (
                    // Check if registration is deactivated (403 means registration deactivated, 404 means event inactive)
                    ((fieldsError as any).isDeactivated || (fieldsError as any).status === 403 || (fieldsError as any).status === 404) ? (
                      <div className="text-center py-12 bg-orange-50 rounded-lg border-2 border-orange-200">
                        <div className="flex justify-center mb-4">
                          <AlertTriangle className="h-16 w-16 text-orange-600" />
                        </div>
                        <p className="text-orange-800 font-semibold text-lg mb-2">
                          Registration Currently Closed
                        </p>
                        <p className="text-sm text-orange-600 mb-4">
                          {(fieldsError as any).status === 404
                            ? "This event is currently inactive or not found."
                            : "Registration for this event has been temporarily deactivated by the organizers."
                          }
                        </p>
                        <div className="bg-orange-100 border border-orange-300 rounded-lg p-4 mb-4 max-w-md mx-auto">
                          <p className="text-sm text-orange-800">
                            <strong>What this means:</strong><br />
                            {(fieldsError as any).status === 404 ? (
                              <>
                                • The event may not exist or is inactive<br />
                                • Check the event URL or link<br />
                                • Contact the organizers to verify event status
                              </>
                            ) : (
                              <>
                                • Registration is temporarily unavailable<br />
                                • The event organizers may reactivate it later<br />
                                • Contact the organizers for more information
                              </>
                            )}
                          </p>
                        </div>
                        <Button
                          onClick={() => window.location.reload()}
                          variant="secondary"
                          className="gap-2"
                        >
                          Check Again
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-red-50 rounded-lg border-2 border-red-200">
                        <div className="flex justify-center mb-4">
                          <AlertCircle className="h-16 w-16 text-red-600" />
                        </div>
                        <p className="text-red-800 font-semibold text-lg mb-2">
                          Failed to load registration form
                        </p>
                        <p className="text-sm text-red-600 mb-4">
                          {(fieldsError as any).status === 404
                            ? "Event not found or registration configuration is missing."
                            : fieldsError.message
                          }
                        </p>
                        {(fieldsError as any).status && (
                          <div className="text-xs text-gray-500 bg-gray-100 p-3 rounded border mb-4">
                            <p><strong>Error Details:</strong></p>
                            <p>Status: {(fieldsError as any).status}</p>
                            <p>Event ID: {selectedEvent}</p>
                          </div>
                        )}
                        <Button
                          onClick={() => window.location.reload()}
                          variant="secondary"
                          className="gap-2"
                        >
                          Try Again
                        </Button>
                      </div>
                    )
                  ) : activeFields.length > 0 ? (
                    <div className="space-y-5">
                      {activeFields.map(field => renderField(field))}

                      {/* Ticket Selection UI */}
                      {currentEvent?.is_ticketing_enabled && publicTickets.length > 0 && (
                        <div className="border-t border-gray-100 pt-8 mt-8">
                          <div className="mb-6">
                            <h3 className="text-xl font-bold text-gray-900">Select Ticket</h3>
                            <p className="text-sm text-gray-500 mt-1">Choose the ticket type that applies to you</p>
                          </div>
                          
                          <div className="space-y-4">
                            {publicTickets.map(ticket => {
                              const isSelected = selectedTicketId === ticket.id;
                              const isFree = ticket.price <= 0;
                              
                              return (
                                <div
                                  key={ticket.id}
                                  onClick={() => {
                                    if (!isSelected) {
                                      setSelectedTicketId(ticket.id);
                                      setTicketQuantity(1);
                                    }
                                  }}
                                  className={`relative rounded-xl border-2 transition-all duration-200 cursor-pointer overflow-hidden ${
                                    isSelected 
                                      ? 'bg-blue-50/30 shadow-md transform scale-[1.01]' 
                                      : 'border-gray-100 bg-white hover:border-gray-200 hover:shadow-sm'
                                  }`}
                                  style={isSelected ? { borderColor: currentEvent?.primary_color || '#3b82f6' } : {}}
                                >
                                  {/* Selection Indicator bar on left */}
                                  <div 
                                    className={`absolute left-0 top-0 bottom-0 w-1.5 transition-all duration-200 ${isSelected ? 'opacity-100' : 'opacity-0'}`}
                                    style={{ backgroundColor: currentEvent?.primary_color || '#3b82f6' }}
                                  />
                                  
                                  <div className="p-5 pl-6">
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1 pr-4">
                                        <div className="flex items-center gap-3 mb-1">
                                          <div className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${
                                            isSelected ? 'border-transparent' : 'border-gray-300'
                                          }`}
                                          style={isSelected ? { backgroundColor: currentEvent?.primary_color || '#3b82f6' } : {}}
                                          >
                                            {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                                          </div>
                                          <h4 className="text-lg font-semibold text-gray-900 leading-tight">{ticket.name}</h4>
                                        </div>
                                        {ticket.description && (
                                          <p className="text-sm text-gray-500 mt-2 ml-8 leading-relaxed">{ticket.description}</p>
                                        )}
                                      </div>
                                      
                                      <div className="text-right flex-shrink-0">
                                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                          isFree ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-900'
                                        }`}>
                                          {isFree ? 'Free' : `GHS ${Number(ticket.price).toFixed(2)}`}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Quantity Selector - smooth expand */}
                                    <div 
                                      className={`ml-8 transition-all duration-300 ease-in-out overflow-hidden ${
                                        isSelected ? 'max-h-40 opacity-100 mt-5 pt-5 border-t border-gray-100' : 'max-h-0 opacity-0 mt-0 pt-0 border-transparent'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <span className="text-sm font-medium text-gray-700 block">Quantity</span>
                                          {ticket.quantity_available !== null && (
                                            <span className="text-xs text-gray-500">{ticket.quantity_available} available</span>
                                          )}
                                        </div>
                                        
                                        <div className="flex items-center gap-4">
                                          <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (ticketQuantity > 1) setTicketQuantity(prev => prev - 1);
                                              }}
                                              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                                              disabled={ticketQuantity <= 1}
                                            >
                                              <span className="text-xl leading-none mb-1">-</span>
                                            </button>
                                            <div className="w-12 h-10 flex items-center justify-center font-semibold text-gray-900 border-x border-gray-100">
                                              {ticketQuantity}
                                            </div>
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (ticket.quantity_available === null || ticketQuantity < ticket.quantity_available) {
                                                  setTicketQuantity(prev => prev + 1);
                                                }
                                              }}
                                              className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                                              disabled={ticket.quantity_available !== null && ticketQuantity >= ticket.quantity_available}
                                            >
                                              <span className="text-xl leading-none mb-1">+</span>
                                            </button>
                                          </div>
                                          
                                          <div className="text-right min-w-[100px]">
                                            <span className="text-xs text-gray-500 block mb-1">Subtotal</span>
                                            <span className="text-lg font-bold text-gray-900">
                                              {isFree ? 'Free' : `GHS ${(ticket.price * ticketQuantity).toFixed(2)}`}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {errors.ticket && (
                            <p className="text-red-500 text-sm mt-3 flex items-center gap-1">
                              <AlertCircle className="w-4 h-4" /> {errors.ticket}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Email Notification Consent - Only show if email field exists AND email_notification_setting is opt_in */}
                      {activeFields.some(field => field.field_type === 'email') && currentEvent?.email_notification_setting === 'opt_in' && (
                        <div className="border-t pt-6 mt-6">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <label className="flex items-start space-x-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={emailConsent}
                                onChange={(e) => setEmailConsent(e.target.checked)}
                                className="mt-1 rounded border-gray-300 focus:ring-opacity-50"
                                style={{ color: currentEvent?.primary_color || '#2563eb', borderColor: currentEvent?.primary_color || '#d1d5db' }}
                              />
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-1">
                                  <Mail className="h-4 w-4" style={{ color: currentEvent?.primary_color || '#2563eb' }} />
                                  <span className="text-sm font-medium text-gray-900">
                                    Send me a confirmation email
                                  </span>
                                </div>
                                <p className="text-xs text-gray-700">
                                  Receive a confirmation email with your registration details,
                                  event information, and QR code for check-in.
                                </p>
                                <p className="text-xs mt-2 font-medium" style={{ color: currentEvent?.primary_color || '#2563eb' }}>
                                  Note: Please check both your inbox and spam/junk folders for the confirmation email.
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="pt-8">
                        <Button
                          type="submit"
                          className="w-full py-4 text-lg gap-3 shadow-lg hover:shadow-xl transition-all duration-200 border-transparent transform hover:-translate-y-0.5 rounded-xl font-bold"
                          loading={registerMutation.isPending || isProcessingPayment}
                          style={{
                            backgroundColor: currentEvent?.primary_color || '#0d9488',
                            color: '#ffffff'
                          }}
                        >
                          <UserPlus className="h-6 w-6" />
                          {isProcessingPayment ? (
                            'Processing Payment...'
                          ) : (
                            selectedTicketId && publicTickets.find(t => t.id === selectedTicketId)?.price > 0
                              ? `Pay GHS ${(publicTickets.find(t => t.id === selectedTicketId).price * ticketQuantity).toFixed(2)}`
                              : 'Complete Registration'
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-yellow-50 rounded-lg border-2 border-yellow-200">
                      <div className="flex justify-center mb-4">
                        <AlertTriangle className="h-16 w-16 text-yellow-600" />
                      </div>
                      <p className="text-yellow-800 font-semibold text-lg mb-2">
                        Registration is currently unavailable
                      </p>
                      <p className="text-sm text-yellow-600 mb-4">
                        No active registration fields found for this event.
                      </p>

                    </div>
                  )}
                </form>
              </div>
            </main>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer variant="registration" customColor={currentEvent?.primary_color} />
    </div>
  );
};

export default SelfRegistration;
